import { List, ListItem, Typography, IconButton, Tooltip, TextField, InputAdornment, Avatar, Badge, ListItemAvatar } from '@mui/material'
import { Box } from '@mui/material'
import { Trans, t } from '@lingui/macro'
import { ChatRoom } from '@/core/models/chat-room.model'
import { Staff } from '@/core/models/staff.model'
import { CreateGroupChat } from './CreateGroupChat'
import { ListItemText } from '@mui/material'
import { useLingui } from '@lingui/react'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { useCallback, useState, useMemo } from 'react'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import GroupsIcon from '@mui/icons-material/Groups'
import { ContactPickerModal } from './ContactPickerModal'

type ChatDrawerProps = {
  staffs: Staff[]
  privateChats: ChatRoom[]
  groupChats: ChatRoom[]
  handleStartPrivateChat: (staff: Staff) => void
  handleGroupCreated: () => void
  handleSelectGroup: (group: ChatRoom) => void
  hasUnreadMessages: (chatRoom: ChatRoom) => boolean
  activeChatRoom: ChatRoom | null
  trackedNavigate: (url: string) => void
  handleStartNewChat: (staffId: string) => void
  onLoadMoreContacts?: () => void
  isLoadingMoreContacts?: boolean
  hasMoreContacts?: boolean
}

export function ChatDrawer({
  staffs,
  privateChats,
  groupChats,
  handleStartPrivateChat,
  handleGroupCreated,
  handleSelectGroup,
  hasUnreadMessages,
  activeChatRoom,
  trackedNavigate,
  handleStartNewChat,
  onLoadMoreContacts,
  isLoadingMoreContacts,
  hasMoreContacts,
}: ChatDrawerProps) {
  useLingui()
  const authUser = useAppSelector(selectAuthUser)
  const [contactPickerOpen, setContactPickerOpen] = useState(false)
  const [groupDialogOpen, setGroupDialogOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  if (!authUser) return null

  // Filter conversations with messages only (WhatsApp-style)
  const conversationsWithMessages = useMemo(() => {
    const allChats = [...(groupChats || []), ...(privateChats || [])]
    return allChats.filter((chat) => chat.messages && chat.messages.length > 0)
  }, [groupChats, privateChats])

  // Helpers
  const formatTimestamp = (iso?: string) => {
    if (!iso) return ''
    const date = new Date(iso)
    const now = new Date()
    const sameDay = date.toDateString() === now.toDateString()
    if (sameDay) {
      return new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' }).format(date)
    }
    return new Intl.DateTimeFormat(undefined, { month: 'short', day: '2-digit' }).format(date)
  }

  const getUnreadCount = (chatRoom: ChatRoom): number => {
    if (!chatRoom?.messages || !authUser?._id) return 0
    return chatRoom.messages.reduce((count, message) => {
      const fromOther = message.author !== authUser._id
      const notRead = !(message.readBy || []).includes(authUser._id)
      return count + (fromOther && notRead ? 1 : 0)
    }, 0)
  }

  // Combine all conversations and sort by most recent message regardless of type
  const sortedConversations = conversationsWithMessages.sort((a, b) => {
    const aLastMessage = a.messages[a.messages.length - 1]
    const bLastMessage = b.messages[b.messages.length - 1]
    const aTime = aLastMessage ? new Date(aLastMessage.date).getTime() : 0
    const bTime = bLastMessage ? new Date(bLastMessage.date).getTime() : 0
    return bTime - aTime // Most recent first
  })

  // Apply search filter to unified conversations
  const normalizedSearch = searchTerm.trim().toLowerCase()
  const filteredConversations = sortedConversations.filter((chat) => {
    const lastMessage = chat.messages[chat.messages.length - 1]?.content?.toLowerCase() || ''

    if (chat.isPrivate) {
      // For private chats, search in the other user's name
      const otherUserId = chat.users.find((u) => u._id !== authUser._id)?._id
      const staff = staffs?.find((s) => s._id === otherUserId)
      const fullName = staff ? `${staff.userInfo.name} ${staff.userInfo.surname}`.toLowerCase() : ''
      return !normalizedSearch || fullName.includes(normalizedSearch) || lastMessage.includes(normalizedSearch)
    } else {
      // For group chats, search in group name
      return !normalizedSearch || chat.name.toLowerCase().includes(normalizedSearch) || lastMessage.includes(normalizedSearch)
    }
  })

  const handlePrivateChatClick = useCallback(
    (staff: Staff) => {
      const existingPrivateChat = privateChats?.find((c) => c.isPrivate && c.users.some((u) => u._id === staff._id))

      if (existingPrivateChat) {
        const targetUrl = `/chat?type=private&id=${existingPrivateChat._id}`
        trackedNavigate(targetUrl)
      } else {
        handleStartPrivateChat(staff)
      }
    },
    [privateChats, handleStartPrivateChat, trackedNavigate]
  )

  const handleNewChatClick = useCallback(() => {
    setContactPickerOpen(true)
  }, [])

  const handleNewGroupClick = useCallback(() => {
    setGroupDialogOpen(true)
  }, [])

  const handleContactSelect = useCallback(
    (staffId: string) => {
      handleStartNewChat(staffId)
      setContactPickerOpen(false)
    },
    [handleStartNewChat]
  )

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        borderRight: '1px solid #e0e0e0',
      }}
    >
      {/* Header with action buttons */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant="h6">
          <Trans>Chats</Trans>
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={<Trans>New chat</Trans>}>
            <IconButton
              size="small"
              onClick={handleNewChatClick}
              aria-label="new chat"
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                width: 36,
                height: 36,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
                '& .MuiSvgIcon-root': { display: 'block' },
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={<Trans>New group</Trans>}>
            <IconButton
              size="small"
              onClick={handleNewGroupClick}
              aria-label="new group"
              sx={{
                backgroundColor: 'info.main',
                color: 'white',
                width: 36,
                height: 36,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
                '& .MuiSvgIcon-root': { display: 'block' },
                '&:hover': {
                  backgroundColor: 'info.dark',
                },
              }}
            >
              <GroupsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Search */}
      <Box sx={{ px: 2, pt: 1 }}>
        <TextField
          fullWidth
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search chats"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Conversations List */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {/* Recent Conversations */}
        {conversationsWithMessages.length > 0 && (
          <>
            <Box sx={{ px: 1, py: 1, color: 'text.secondary', fontSize: '0.875rem', fontWeight: 600 }}>
              <Trans>Recent Conversations</Trans>
            </Box>

            <List dense>
              {/* Unified Conversations - both private and group sorted by most recent */}
              {filteredConversations.map((chat: ChatRoom) => {
                const hasUnread = hasUnreadMessages(chat)
                const isActive = activeChatRoom?._id === chat._id
                const unreadCount = getUnreadCount(chat)
                const lastMessage = chat.messages[chat.messages.length - 1]

                if (chat.isPrivate) {
                  // Render private chat
                  const otherUserId = chat.users.find((u: Partial<Staff>) => u._id !== authUser._id)?._id
                  const staff = staffs?.find((s) => s._id === otherUserId)

                  if (!staff) return null

                  return (
                    <ListItem
                      key={chat._id}
                      onClick={() => handlePrivateChatClick(staff)}
                      sx={{
                        cursor: 'pointer',
                        py: 0.5,
                        px: 1,
                        borderRadius: 1,
                        backgroundColor: isActive ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                        '&:hover': {
                          backgroundColor: isActive ? 'rgba(0, 0, 0, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Badge color="primary" overlap="circular" badgeContent={unreadCount > 0 ? unreadCount : 0} invisible={unreadCount === 0}>
                          <Avatar>{`${staff.userInfo.name?.[0] || ''}${staff.userInfo.surname?.[0] || ''}`.toUpperCase()}</Avatar>
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontWeight: hasUnread && !isActive ? 'bold' : 'normal' }}>
                              {`${staff.userInfo.name} ${staff.userInfo.surname}`}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                              {formatTimestamp(lastMessage?.date)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            sx={{
                              color: hasUnread && !isActive ? 'primary.main' : 'text.secondary',
                              fontWeight: hasUnread && !isActive ? 'bold' : 'normal',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {lastMessage?.content || ''}
                          </Typography>
                        }
                      />
                    </ListItem>
                  )
                } else {
                  // Render group chat
                  return (
                    <ListItem
                      key={chat._id}
                      onClick={() => handleSelectGroup(chat)}
                      sx={{
                        cursor: 'pointer',
                        py: 0.5,
                        px: 1,
                        borderRadius: 1,
                        backgroundColor: isActive ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                        '&:hover': {
                          backgroundColor: isActive ? 'rgba(0, 0, 0, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Badge color="primary" overlap="circular" badgeContent={unreadCount > 0 ? unreadCount : 0} invisible={unreadCount === 0}>
                          <Avatar>{chat.name?.[0]?.toUpperCase() || 'G'}</Avatar>
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontWeight: hasUnread && !isActive ? 'bold' : 'normal' }}>{chat.name}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                              {formatTimestamp(lastMessage?.date)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: hasUnread && !isActive ? 'primary.main' : 'text.secondary',
                                fontWeight: hasUnread && !isActive ? 'bold' : 'normal',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                              }}
                            >
                              {lastMessage?.content || ''}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                              {chat.users.length}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  )
                }
              })}
            </List>
          </>
        )}

        {/* Empty State */}
        {conversationsWithMessages.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '60%',
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            <Box sx={{ mb: 2, fontSize: '0.875rem', fontWeight: 400 }}>
              <Trans>No conversations yet</Trans>
            </Box>
            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              <Trans>Start a new chat or create a group to get started</Trans>
            </Box>
          </Box>
        )}
      </Box>

      {/* Contact Picker Modal */}
      <ContactPickerModal
        open={contactPickerOpen}
        onClose={() => setContactPickerOpen(false)}
        staffs={staffs || []}
        onContactSelect={handleContactSelect}
        currentUserId={authUser._id}
        existingPrivateChats={privateChats || []}
        onLoadMore={onLoadMoreContacts}
        isLoadingMore={isLoadingMoreContacts}
        hasMore={hasMoreContacts}
      />

      {/* Create Group Dialog (controlled) */}
      <CreateGroupChat
        creatorId={authUser._id}
        staffs={staffs || []}
        onGroupCreated={handleGroupCreated}
        hideTrigger
        open={groupDialogOpen}
        onOpenChange={setGroupDialogOpen}
      />
    </Box>
  )
}
